<template>
  <div
    class="w-full lg:sticky top-[100px] lg:max-w-[480px] shrink-0 bg-surface-grey-light rounded-md p-6 space-y-6"
    ref="ticketAreaRef"
  >
    <div
      v-if="event.status_message_visible"
      class="bg-surface-light p-4 border-surface-grey border rounded-md flex items-start gap-2"
    >
      <InfoIcon class="w-5 h-5 shrink-0 fill-primary mt-[2px]" />
      <div class="font-semibold">
        {{ props.event.status_message_text }}
      </div>
    </div>

    <div class="relative -mx-6 dashed-ticket-border" v-if="event.status_message_visible">
      <svg
        :class="[
          'absolute z-10 bottom-[1px] -right-[1px] translate-y-1/2 fill-surface-light ',
        ]"
        xmlns="http://www.w3.org/2000/svg"
        width="7"
        height="16"
        viewBox="0 0 7 16"
      >
        <path
          d="M7.5 0.518555C3.59105 0.775917 0.5 4.02593 0.5 8C0.5 11.9741 3.59105 15.2241 7.5 15.4814V0.518555Z"
        />
      </svg>

      <svg
        :class="[
          'absolute bottom-[1px] -left-[1px] z-10 translate-y-1/2 fill-surface-light ',
        ]"
        xmlns="http://www.w3.org/2000/svg"
        width="7"
        height="16"
        viewBox="0 0 7 16"
        fill="none"
      >
        <path
          d="M-0.5 0.518555C3.40895 0.775917 6.5 4.02593 6.5 8C6.5 11.9741 3.40895 15.2241 -0.5 15.4814V0.518555Z"
        />
      </svg>
    </div>

    <div class="space-y-1">
      <div class="flex items-baseline space-x-2">
        <span class="h3" v-if="event.price_override_active">
          {{ getLocalizedEventValue(event, locale, 'price_override_text') }}
        </span>
        <span v-else>
          <span class="h3" v-if="event.min_price_from">
            {{ $t('common.from') }} {{ ' ' }}
          </span>
          <span class="h3" v-if="!event.hide_price">
            {{ formatPrice(event.price, event.currency, $i18n.locale) }}
          </span>
        </span>
        <span
          v-if="priceBefore"
          class="text-text line-through font-semibold"
        >
          {{ $t('common.before') }}
          {{ formatPrice(priceBefore, event.currency, $i18n.locale) }}
        </span>
      </div>
              

    </div>

    <BaseButton
      key="static"
      class="w-full relative group"
      :color="buttonState.color"
      size="lg"
      :disabled="!buttonState.active"
      @click="handleTicketClick"
    >
      <span class="w-full flex items-center justify-between">
        <span class="text-wrap">{{ buttonState.text }}</span>
        <ArrowRightIcon
          class="w-4 h-4 ml-2 fill-current group-hover:animate-pulse-right"
          v-if="buttonState.active"
        />
      </span>
    </BaseButton>

    <transition name="slidedown" mode="out-in">
      <div
        v-if="isFixed"
        class="fixed mb-0 bottom-0 border-t border-surface-grey z-10 left-0 right-0 p-4 md:hidden bg-surface-light"
      >
        <BaseButton
          class="w-full"
          :color="buttonState.color"
          size="lg"
          :disabled="!buttonState.active"
          @click="handleTicketClick"
        >
          <span class="w-full flex items-center justify-between">
            <span class="text-wrap"> {{ buttonState.text }} </span>
            <ArrowRightIcon 
              class="w-4 h-4 ml-2 fill-current" 
              v-if="buttonState.active" 
            />
          </span>
        </BaseButton>
      </div>
    </transition>

    <div class="flex flex-col gap-2 md:flex-row md:items-center justify-between">
      <div v-if="event.remaining_tickets">
        <div class="flex items-center text-sm font-semibold" v-if="event.saleend && event.saleend_countdown && !saleEnded && !saleNotStarted">
          <StopwatchIcon class="w-5 h-5 fill-current mr-1" />
          <span class="mr-1">{{ $t('event.ends_in') }}</span>
          <BaseCountdown v-if="event.saleend" :date="event.saleend" />
        </div>
      </div>

      <div class="flex items-center" v-if="event.remaining_tickets && event.show_remaining_tickets"> 
        <TicketIcon class="w-5 h-5 fill-current mr-1" />
        <span class="text-sm font-semibold">
          {{ $t('event.x_tickets_left', { count: event.remaining_tickets }) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useCartStore } from '@/stores/cart';
import { useCheckoutStore } from '@/stores/checkoutStore';
import type { EventDetail } from '@/types';
import { getLocalizedEventValue, getSalestart } from '@/utils/eventItemUtil';
import { formatDate } from '@/utils/formatDate';
import { formatPrice } from '@/utils/formatPrice';
import { unixTimeToDate } from '@/utils/unixTimeToDate';

import ArrowRightIcon from '../icons/ArrowRightIcon.vue';
import InfoIcon from '../icons/InfoIcon.vue';
import StopwatchIcon from '../icons/StopwatchIcon.vue';
import TicketIcon from '../icons/TicketIcon.vue';
import BaseButton, { type ButtonColor } from '../ui/BaseButton.vue';
import BaseCountdown from '../ui/BaseCountdown.vue';

const props = defineProps<{
  event: EventDetail;
}>();

const { locale, t: $t } = useI18n();

const isFixed = ref(false);
const ticketAreaRef = ref<HTMLElement | null>(null);
const now = ref(new Date());

let nowInterval: ReturnType<typeof setInterval>;

const cart = useCartStore();
const checkout = useCheckoutStore();

const priceBefore = computed(() => {
  const usertype = 'jpoplev'; // TODO get from sso module
  return props.event.sso_prices[usertype]?.price;
}); 

function handleTicketClick() {
  if (!buttonState.value.active) {
    return;
  }
  checkout.setCheckoutID(props.event.id);
  cart.addToCart(props.event.id);
}

function handleScroll() {
  if (ticketAreaRef.value) {
    const rect = ticketAreaRef.value.getBoundingClientRect();
    const hasScrolledPast = rect.bottom <= 0;
    isFixed.value = hasScrolledPast;
  }
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
  handleScroll();
  nowInterval = setInterval(() => { now.value = new Date(); }, 60_000);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  clearInterval(nowInterval);
});

// "find tickets" button
type ButtonState = { color: ButtonColor; active: boolean; text: string };

const saleEnded = computed(() => props.event.saleend && unixTimeToDate(props.event.saleend) < now.value);
const saleNotStarted = computed(() => props.event.salestart && unixTimeToDate(props.event.salestart) > now.value);

const buttonState = computed<ButtonState>(() => {  
  if (props.event.soldout) {
    return { color: 'muted', active: false, text: $t('event.sold_out') };
  }
  if (saleNotStarted.value) {
    const formated_date = formatDate(getSalestart(props.event), { locale: locale.value, showTime: true });
    if (formated_date && formated_date !== 'Invalid date') {
      return { color: 'muted', active: false, text: $t('event.sale_not_started_with_date', { date: formated_date }) };
    }else {
      return { color: 'muted', active: false, text: $t('event.sale_not_started') };
    }
  }
  if (saleEnded.value) {
    return { color: 'muted', active: false, text: $t('event.sale_ended') };
  }
  console.log("all good in the hood");
  
  return {
    color: 'primary',
    active: true,
    text: getLocalizedEventValue(props.event, locale.value, 'button_text_find_tickets') || $t('event.find_tickets'),
  };
});

</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
